import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, X, Image as ImageIcon, Upload, ExternalLink } from "lucide-react";
import { api, API, getToken } from "../../../lib/auth";
import { useParams } from "react-router-dom";

function Section({ num, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 lg:p-7 mb-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-semibold text-black text-base">{title}</h3>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-700 text-white">#{num}</span>
      </div>
      {children}
    </div>
  );
}

function Field({ lbl, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-black mb-2">{lbl}</label>
      {children}
      {hint && <div className="text-[11px] text-black/50 mt-1">{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, type = "text", placeholder, testid }) {
  return (
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white" />
  );
}

function Toggle({ value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${value ? "border-[#1FA868] bg-[#E8F5E9]" : "border-black/15 bg-white"}`}>
      <span className={`w-4 h-4 rounded-sm ${value ? "bg-[#1FA868]" : "bg-black/15"}`} />
      <span className="text-sm font-semibold text-black">{value ? "Enabled" : "No"}</span>
    </button>
  );
}

function FileInput({ url, onChange, kind, testid }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      const r = await fetch(`${API}/t/store/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const j = await r.json();
      if (j.url) {
        onChange(j.url);
        toast.success("Uploaded");
      } else {
        toast.error(j.detail || "Upload failed");
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <label className={`flex-1 inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-black/15 bg-white cursor-pointer text-sm ${busy ? "opacity-50" : ""}`}>
        <span className="text-black/55 truncate">{url ? url.split("/").pop() : "No file chosen"}</span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/5 text-xs font-semibold text-black/70">
          <Upload size={12} /> Browse
        </span>
        <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={testid} disabled={busy} />
      </label>
      {url && (
        <div className="w-12 h-12 rounded-lg bg-[#F4F5FA] overflow-hidden border border-black/10 flex items-center justify-center">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}

export default function StoreSettings() {
  const { subdomain } = useParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/t/store/settings").then((r) => setS(r.data)); }, []);

  const upd = (patch) => setS((p) => ({ ...p, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/t/store/settings", s);
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="p-10 text-black/50">Loading…</div>;

  return (
    <div data-testid="store-settings-page" className="p-6 lg:p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Settings</h1>
          <div className="text-sm text-black/55 mt-1">Store · Settings</div>
        </div>
        <a href={`/store/${subdomain}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-black/10 text-sm font-semibold text-black hover:border-black/25">
          <ExternalLink size={13} /> Visit Online Store
        </a>
      </div>

      <Section num="1" title="Store basics">
        <div className="grid md:grid-cols-3 gap-5">
          <Field lbl="Online Store URL" hint="When disabled, the /store pages will be inaccessible.">
            <Toggle value={s.online_store_enabled} onChange={(v) => upd({ online_store_enabled: v })} testid="ss-enabled" />
          </Field>
          <Field lbl="Store name">
            <TextInput value={s.store_name} onChange={(v) => upd({ store_name: v })} testid="ss-name" />
          </Field>
          <Field lbl="Currency" hint="⚠ Changing currency will affect both system and online store">
            <select value={s.currency} onChange={(e) => upd({ currency: e.target.value })} data-testid="ss-currency"
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white">
              <option value="NGN">Naira (₦)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </Field>

          <Field lbl="Default Warehouse">
            <TextInput value={s.default_warehouse} onChange={(v) => upd({ default_warehouse: v })} testid="ss-warehouse" />
          </Field>
          <Field lbl="Primary color">
            <input type="color" value={s.primary_color} onChange={(e) => upd({ primary_color: e.target.value })}
              data-testid="ss-primary"
              className="w-full h-11 rounded-lg border border-black/15 cursor-pointer" />
          </Field>
          <Field lbl="Secondary color">
            <input type="color" value={s.secondary_color} onChange={(e) => upd({ secondary_color: e.target.value })}
              data-testid="ss-secondary"
              className="w-full h-11 rounded-lg border border-black/15 cursor-pointer" />
          </Field>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <Field lbl="Font family">
            <TextInput value={s.font_family} onChange={(v) => upd({ font_family: v })} testid="ss-font" />
          </Field>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <div className="font-semibold text-sm text-black mb-2">Stock behavior</div>
            <Toggle value={s.allow_overselling} onChange={(v) => upd({ allow_overselling: v })} testid="ss-overselling" />
            <div className="text-[11px] text-black/50 mt-1">When disabled, customers cannot add more than available stock to the cart.</div>
          </div>
          <div>
            <div className="font-semibold text-sm text-black mb-2">Hide out-of-stock products</div>
            <Toggle value={s.hide_out_of_stock} onChange={(v) => upd({ hide_out_of_stock: v })} testid="ss-hideoos" />
          </div>
          <div>
            <div className="font-semibold text-sm text-black mb-2">Hide prices for guests</div>
            <Toggle value={s.hide_prices_for_guests} onChange={(v) => upd({ hide_prices_for_guests: v })} testid="ss-hideprices" />
            <div className="text-[11px] text-black/50 mt-1">When enabled, product prices are only visible to logged-in customers.</div>
          </div>
        </div>
      </Section>

      <Section num="2" title="Contact">
        <div className="grid md:grid-cols-3 gap-5">
          <Field lbl="Contact email"><TextInput type="email" value={s.contact_email} onChange={(v) => upd({ contact_email: v })} testid="ss-cemail" /></Field>
          <Field lbl="Contact phone"><TextInput value={s.contact_phone} onChange={(v) => upd({ contact_phone: v })} testid="ss-cphone" /></Field>
          <Field lbl="Contact address"><TextInput value={s.contact_address} onChange={(v) => upd({ contact_address: v })} testid="ss-caddr" /></Field>
        </div>
      </Section>

      <Section num="3" title="Branding">
        <div className="grid md:grid-cols-2 gap-5">
          <Field lbl="Logo"><FileInput url={s.logo_url} onChange={(u) => upd({ logo_url: u })} kind="logo" testid="ss-logo" /></Field>
          <Field lbl="Favicon"><FileInput url={s.favicon_url} onChange={(u) => upd({ favicon_url: u })} kind="favicon" testid="ss-favicon" /></Field>
        </div>
      </Section>

      <Section num="4" title="Hero header">
        <div className="space-y-4">
          <Field lbl="Hero title"><TextInput value={s.hero_title} onChange={(v) => upd({ hero_title: v })} testid="ss-htitle" /></Field>
          <Field lbl="Hero subtitle">
            <textarea value={s.hero_subtitle} onChange={(e) => upd({ hero_subtitle: e.target.value })} rows={3}
              data-testid="ss-hsub"
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white" />
          </Field>
          <Field lbl="Hero image"><FileInput url={s.hero_image} onChange={(u) => upd({ hero_image: u })} kind="hero" testid="ss-hero" /></Field>
        </div>
      </Section>

      <Section num="5" title="SEO">
        <div className="space-y-4">
          <Field lbl="SEO title"><TextInput value={s.seo_title} onChange={(v) => upd({ seo_title: v })} testid="ss-seotitle" /></Field>
          <Field lbl="SEO description">
            <textarea value={s.seo_description} onChange={(e) => upd({ seo_description: e.target.value })} rows={3}
              data-testid="ss-seodesc"
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white" />
          </Field>
        </div>
      </Section>

      <Section num="6" title="Topbar & footer">
        <div className="grid md:grid-cols-2 gap-5">
          <Field lbl="Topbar text (left)"><TextInput value={s.topbar_left} onChange={(v) => upd({ topbar_left: v })} testid="ss-tbl" /></Field>
          <Field lbl="Topbar text (right)"><TextInput value={s.topbar_right} onChange={(v) => upd({ topbar_right: v })} testid="ss-tbr" /></Field>
        </div>
        <div className="mt-4">
          <Field lbl="Footer text">
            <textarea value={s.footer_text} onChange={(e) => upd({ footer_text: e.target.value })} rows={2}
              data-testid="ss-footer"
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white" />
          </Field>
        </div>
      </Section>

      <Section num="7" title="Social links">
        <div className="space-y-2">
          {(s.social_links || []).map((l, i) => (
            <div key={l.id || i} className="flex items-center gap-2">
              <TextInput value={l.platform} onChange={(v) => {
                const arr = [...s.social_links]; arr[i] = { ...arr[i], platform: v }; upd({ social_links: arr });
              }} placeholder="facebook" />
              <TextInput value={l.url} onChange={(v) => {
                const arr = [...s.social_links]; arr[i] = { ...arr[i], url: v }; upd({ social_links: arr });
              }} placeholder="https://facebook.com/yourshop" />
              <button type="button" onClick={() => upd({ social_links: s.social_links.filter((_, k) => k !== i) })}
                className="p-2.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white">
                <X size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => upd({ social_links: [...(s.social_links || []), { id: Date.now() + "", platform: "", url: "" }] })}
            data-testid="ss-add-social"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-[#22C55E] text-[#22C55E] text-sm font-semibold hover:bg-[#F0FDF4]">
            <Plus size={14} /> Add link
          </button>
        </div>
      </Section>

      <Section num="8" title="Homepage blocks">
        <div className="space-y-2">
          {(s.homepage_blocks || []).map((b, i) => (
            <div key={b.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-black/10 bg-white">
              <div>
                <div className="font-semibold text-sm text-black">{b.label}</div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#1FA868] bg-[#E8F5E9] px-1.5 py-0.5 rounded">{b.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <Toggle value={b.active} onChange={(v) => {
                  const arr = [...s.homepage_blocks]; arr[i] = { ...arr[i], active: v }; upd({ homepage_blocks: arr });
                }} />
                <button onClick={() => {
                  if (i === 0) return;
                  const arr = [...s.homepage_blocks]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; upd({ homepage_blocks: arr });
                }} className="px-2 py-1.5 rounded-md bg-black/5 text-xs">↑</button>
                <button onClick={() => {
                  if (i === s.homepage_blocks.length - 1) return;
                  const arr = [...s.homepage_blocks]; [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; upd({ homepage_blocks: arr });
                }} className="px-2 py-1.5 rounded-md bg-black/5 text-xs">↓</button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Sticky save bar */}
      <div className="bg-white rounded-2xl border border-black/5 p-5 flex items-center justify-between sticky bottom-4 shadow-lg">
        <div className="text-sm text-black/55">Unsaved changes may be lost</div>
        <button onClick={save} disabled={busy} data-testid="ss-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm hover:opacity-95 disabled:opacity-60 shadow-md">
          <Save size={14} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
